/**
 * `PixyzConversionModel.ts` — cloud CAD conversion (Pixyz) for the web port.
 *
 * Port of `AwsPixyzImportProcessor` + `AwsImporterModel-JobMgmt` (the
 * no-DataProxy path). A CAD file (STEP / SLDPRT / IGES / JT / CATPart / …)
 * can't be parsed in-browser, so it's converted in the cloud:
 *
 *   1. upload the CAD bytes to S3 at `account/{c}/projectdata/{guid}.glb.{ext}`
 *   2. `StartExecution` on the Pixyz "genericpolygon-campfire-params" state
 *      machine (the `NEW_PIXYZ` variant) — reads the input key, writes
 *      `{guid}.glb`
 *   3. poll `DescribeExecution` until the execution leaves `RUNNING`
 *   4. download `{guid}.glb` — a standard GLB the {@link WebImporterModel} reads
 *
 * The AWS Step Functions calls use the same Cognito Identity-Pool credentials
 * as the S3 / Dynamo clients (`aws-amplify` `fetchAuthSession`). This requires
 * the identity IAM role to permit `states:StartExecution` +
 * `states:DescribeExecution` — {@link probeAccess} verifies that.
 */

import { randomUuid } from "@models/utils/uuid";
import {
  SFNClient,
  StartExecutionCommand,
  DescribeExecutionCommand,
  type ExecutionStatus,
} from "@aws-sdk/client-sfn";
import { fetchAuthSession } from "aws-amplify/auth";
import { Ioc } from "@core/ioc/ioc";
import {
  loadCloudConfig,
  polygonStateMachineArn,
  type CloudConfig,
} from "@models/storage/cloudConfig";
import { registerAwsCredentialClientReset } from "@models/storage/awsCredentialClientRegistry";
import type { IStorageModel } from "@models/storage/IStorageModel";
import { STORAGE_MODEL } from "@models/storage/IStorageModel";
import type { IAuthModel } from "@models/auth/IAuthModel";
import { AUTH_MODEL } from "@models/auth/IAuthModel";
import type { IImporterModel, ParsedModel } from "./IImporterModel";
import { IMPORTER_MODEL } from "./IImporterModel";
import type { IPendingImportsModel, PendingImportStatus } from "./IPendingImportsModel";
import { PENDING_IMPORTS_MODEL } from "./IPendingImportsModel";
import type { IProjectModel } from "@models/project/IProjectModel";
import { PROJECT_MODEL } from "@models/project/ProjectModel";

/** Coarse conversion phase, for progress copy in the UI. */
export type ConversionPhase = "uploading" | "converting" | "downloading";

export interface ConversionProgress {
  readonly phase: ConversionPhase;
  /** 0..1 overall. */
  readonly progress: number;
}

const K_POLL_INTERVAL_MS = 10_000; // Unity `checkIntervalSeconds = 10`
const K_MAX_WAIT_MS = 7_200_000; // Unity `kMaxSecondsWaitPixyz` = 2 hours
const K_OUTPUT_EXT = ".glb";

/**
 * CAD extensions Pixyz converts in the cloud (from
 * `AwsPixyzImportProcessor.supportedPixyzExtensions`, minus the browser-native
 * formats the web already imports directly). Lowercased, with dot.
 */
export const PIXYZ_CAD_EXTENSIONS: ReadonlyArray<string> = [
  ".stp", ".step", ".stpz", ".stepz",
  ".sldprt", ".sldasm",
  ".igs", ".iges",
  ".jt",
  ".catpart", ".catproduct", ".catshape", ".cgr", ".3dxml", ".model", ".session",
  ".prt", ".asm", ".neu", ".xas", ".xpr",
  ".x_b", ".x_t", ".p_t", ".p_b", ".xmt", ".xmt_txt", ".xmt_bin",
  ".3ds", ".sat", ".sab", ".dwg", ".dxf",
  ".ifc", ".prc", ".3dm", ".rvm", ".rvt", ".rfa",
  ".par", ".pwd", ".psm",
  ".vda", ".wrl", ".vrml", ".u3d",
  ".fbx", // enterprise / old-version FBX also route through Pixyz upstream
];

export class PixyzConversionModel {
  private readonly m_config: CloudConfig = loadCloudConfig();
  private m_sfn: SFNClient | null = null;

  constructor() {
    registerAwsCredentialClientReset(() => {
      this.m_sfn?.destroy();
      this.m_sfn = null;
    });
  }

  private getSfnClient(): SFNClient {
    if (this.m_sfn) return this.m_sfn;
    this.m_sfn = new SFNClient({
      region: this.m_config.region,
      credentials: async () => {
        const session = await fetchAuthSession();
        const creds = session.credentials;
        if (!creds || !creds.accessKeyId || !creds.secretAccessKey) {
          throw new Error("[PixyzConversionModel] No AWS credentials — sign in first.");
        }
        return {
          accessKeyId: creds.accessKeyId,
          secretAccessKey: creds.secretAccessKey,
          sessionToken: creds.sessionToken,
          expiration: creds.expiration,
        };
      },
    });
    return this.m_sfn;
  }

  /** True when this file needs cloud conversion (a Pixyz CAD format). */
  isCadFile(fileName: string): boolean {
    return PIXYZ_CAD_EXTENSIONS.includes(extensionOf(fileName));
  }

  /**
   * End-to-end CAD import: cloud-convert → parse → spawn, driving a hierarchy
   * loading row (`IPendingImportsModel`) across the phases. The resulting GLB
   * flows through the SAME `WebImporterModel` → `ProjectModel.importModelAsync`
   * pipeline "Add Model" uses. Returns the new asset/entity ids.
   */
  async convertAndImportAsync(
    file: File,
    signal?: AbortSignal,
  ): Promise<{ parsed: ParsedModel; assetId: string; entityId: string }> {
    const auth = Ioc.resolve<IAuthModel>(AUTH_MODEL);
    const project = Ioc.resolve<IProjectModel>(PROJECT_MODEL);
    const importer = Ioc.resolve<IImporterModel>(IMPORTER_MODEL);
    const pending = Ioc.resolve<IPendingImportsModel>(PENDING_IMPORTS_MODEL);

    const customerId = auth.currentUser?.customerId;
    if (!customerId) throw new ConversionError("Sign in to import CAD files.");
    if (!project.activeProject || !project.activeScene) {
      throw new ConversionError("Open a scene before importing a model.");
    }
    const sceneId = project.activeScene.id;
    const rowId = randomUuid();
    pending.begin({ id: rowId, displayName: file.name, sceneId });
    try {
      // Convert (upload → StartExecution → poll → download). Map the 0..1
      // conversion progress into the row's 0..0.9 band, leaving 0.9..1 for the
      // parse + spawn step.
      const glbFile = await this.convertCadToGlbAsync(
        file,
        customerId,
        (p) => pending.update(rowId, Math.min(0.9, p.progress * 0.9), phaseToStatus(p.phase)),
        signal,
      );
      pending.update(rowId, 0.92, "importing");
      const parsed = await importer.importFromFileAsync(glbFile, signal, {
        forceMetersScale: true,
      });
      const ids = await project.importModelAsync(parsed, signal);
      pending.update(rowId, 1, "done");
      return { parsed, ...ids };
    } finally {
      // Clear the loading row on success (the real entity row is already
      // present via onEntityChangesApplied) OR on failure / cancel.
      pending.remove(rowId);
    }
  }

  /**
   * Convert a CAD file to GLB in the cloud and return the resulting GLB as a
   * `File` (ready for {@link WebImporterModel.importFromFileAsync}). Mirrors
   * `AwsPixyzImportProcessor.StartImportAsync` + `ContinueImportAsync` (the
   * no-DataProxy path): upload → StartExecution → poll DescribeExecution →
   * download `{guid}.glb`.
   *
   * S3 upload/download reuse `IStorageModel` (same customerassets bucket).
   */
  async convertCadToGlbAsync(
    file: File,
    customerId: string,
    onProgress?: (p: ConversionProgress) => void,
    signal?: AbortSignal,
  ): Promise<File> {
    const inputExt = extensionOf(file.name);
    if (!PIXYZ_CAD_EXTENSIONS.includes(inputExt)) {
      throw new ConversionError(`Unsupported CAD format "${inputExt}".`);
    }
    const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);
    const guid = randomUuid();
    // Input key encodes the target format (Unity `inObjKey = guid + outExt + inExt`).
    const srcKey = `${guid}${K_OUTPUT_EXT}${inputExt}`; // {guid}.glb.sldprt
    const destKey = `${guid}${K_OUTPUT_EXT}`; // {guid}.glb

    throwIfAborted(signal);
    onProgress?.({ phase: "uploading", progress: 0.02 });
    await storage.putAssetObject(customerId, srcKey, file, signal);
    onProgress?.({ phase: "uploading", progress: 0.1 });

    // Start the Pixyz "generic polygon" conversion (Unity StartConversionJob).
    const suffix = randomSuffix();
    const execName = `${guid}-${suffix}`;
    const input = JSON.stringify({
      JobName: execName,
      OUTPUT_FILE_KEY: `account/${customerId}/projectdata/${destKey}`,
      MODEL_NAME: guid,
      ISMULTI: "False",
      INPUT_FILE_KEY: `account/${customerId}/projectdata/${srcKey}`,
      BUCKET_NAME: this.m_config.projectBucket,
      REGION: this.m_config.region,
      PARAMS: this.m_config.pixyzParams,
    });
    const started = await this.getSfnClient().send(
      new StartExecutionCommand({
        stateMachineArn: polygonStateMachineArn(this.m_config),
        name: execName,
        input,
      }),
      { abortSignal: signal },
    );
    const executionArn = started.executionArn;
    if (!executionArn) throw new ConversionError("Failed to start the conversion job.");
    onProgress?.({ phase: "converting", progress: 0.15 });

    // Poll until the execution leaves RUNNING (Unity CheckJobStatus).
    // Progress here is a time-based estimate — Step Functions gives no %.
    const startedAt = Date.now();
    // ~1 min per 10 MB, floor 30 s (Unity `expectedSeconds`).
    const expectedMs = Math.max(30_000, (60_000 * file.size) / 10_000_000);
    for (;;) {
      throwIfAborted(signal);
      const described = await this.getSfnClient().send(
        new DescribeExecutionCommand({ executionArn }),
        { abortSignal: signal },
      );
      const status = described.status;
      if (status && status !== "RUNNING" && status !== "PENDING_REDRIVE") {
        if (status !== "SUCCEEDED") {
          throw new ConversionError(`Cloud conversion ${String(status).toLowerCase()}.`);
        }
        break;
      }
      const elapsed = Date.now() - startedAt;
      if (elapsed > K_MAX_WAIT_MS) {
        throw new ConversionError("Cloud conversion timed out.");
      }
      // Map elapsed/expected into the 0.15..0.8 band, easing toward (not past) 0.8.
      const frac = Math.min(0.99, elapsed / expectedMs);
      onProgress?.({ phase: "converting", progress: 0.15 + frac * 0.65 });
      await delay(K_POLL_INTERVAL_MS, signal);
    }

    onProgress?.({ phase: "downloading", progress: 0.85 });
    const blob = await storage.getAssetBlobUrl(customerId, destKey, signal);
    if (!blob) throw new ConversionError("Converted model not found in storage.");
    let glb: Blob;
    try {
      glb = await (await fetch(blob.url)).blob();
    } finally {
      URL.revokeObjectURL(blob.url);
    }
    onProgress?.({ phase: "downloading", progress: 1 });
    const glbName = file.name.slice(0, file.name.length - inputExt.length) + K_OUTPUT_EXT;
    return new File([glb], glbName, { type: "model/gltf-binary" });
  }

  dispose(): void {
    this.m_sfn?.destroy();
    this.m_sfn = null;
  }
}

export const PIXYZ_CONVERSION_MODEL = Symbol.for("Campfire.PixyzConversionModel");

export class ConversionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ConversionError";
  }
}

function phaseToStatus(phase: ConversionPhase): PendingImportStatus {
  return phase;
}

function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf(".");
  return dot >= 0 ? fileName.slice(dot).toLowerCase() : "";
}

function randomSuffix(): string {
  return randomUuid().replace(/-/g, "").slice(0, 6);
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(t);
      reject(new DOMException("Aborted", "AbortError"));
    };
    if (signal?.aborted) {
      clearTimeout(t);
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
}

// Re-export the AWS status enum type for downstream polling logic.
export type { ExecutionStatus };
